<template>
    <div class="py-4 container-fluid">
      <div class=" row">
        <div class="col-12">
            <div class="card">
                <div v-if="isLoading" class="text-center p-5">
                    <div class="spinner-border text-success" role="status">
                        <span class="visually-hidden">Cargando...</span>
                    </div>
                </div>
                <div v-else class="table-responsive p-0">
                    <ClientsTable :clients="clients" :invoices="pagedRows"/>
                </div>

                <div v-if="!isLoading && totalRecords > 0" class="card-footer d-flex justify-content-between align-items-center">
                    <small class="text-muted">
                        Mostrando {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage, filteredRows.length) }} de {{ filteredRows.length }} registros filtrados.
                    </small>
                    <argon-button
                        color="danger"
                        size="sm"
                        @click="descargarTodosLosPDFs"
                        :disabled="isLoading || filteredRows.length === 0"
                    >
                        <i class="fas fa-file-pdf me-2"></i> Descargar PDFs
                    </argon-button>
                    <nav aria-label="Page navigation example">
                        <ul class="pagination pagination-info">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">
                                    <i class="fa fa-angle-left"></i>
                                </a>
                            </li>
                            <li
                                class="page-item"
                                v-for="page in visiblePages"
                                :key="page"
                                :class="{ active: currentPage === page }"
                            >
                                <a class="page-link" href="#" @click.prevent="changePage(page)">{{ page }}</a>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                                <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">
                                    <i class="fa fa-angle-right"></i>
                                </a>
                            </li>
                        </ul>
                    </nav>
                </div>

            </div>
        </div>
      </div>
    </div>
</template>

<script>
import axios from "axios";
import ClientsTable from "./components/Table.vue";
import { useDTEFiscal } from '@/composables/useDTEFiscal';
import ArgonButton from "@/components/ArgonButton.vue";
import { useCompanyConfig } from '@/composables/useCompanyConfig';

const companyId = localStorage.getItem('companyId');
export default {
    name: "InvoiceTablesView",
    components: {
        ClientsTable,
        ArgonButton
    },
    setup(){
        const { companyConfig,isLoadingCiaConfig, fetchCompanyConfig } = useCompanyConfig();
        // Cargar la configuración cada vez que el componente se monte/cargue
        fetchCompanyConfig();
        return { companyConfig ,isLoadingCiaConfig};
    },
    data() {
        return {
            clients: [],
            clientsBulk:[],
            invoices: [], // Almacena TODAS las facturas cargadas desde el backend
            searchTerm: '',
            currentPage: 1,
            itemsPerPage: 10,
            totalRecords: 0, // Total de registros cargados (invoices.length)
            isLoading: true,
        };
    },
    computed: {
        // 1. FILTRADO: Aplica la búsqueda a todas las facturas
        filteredRows() {
            if (!this.searchTerm) {
                return this.invoices;
            }
            const searchLower = this.searchTerm.toLowerCase();
            return this.invoices.filter(invoice => {
                // Combina campos relevantes para la búsqueda
                const fullNumber = invoice.full_number ? invoice.full_number.toString().toLowerCase() : '';
                const clientName = invoice.client_name ? invoice.client_name.toString().toLowerCase() : '';
                const clientNit = invoice.client_nit ? invoice.client_nit.toString().toLowerCase() : '';

                return fullNumber.includes(searchLower) ||
                       clientName.includes(searchLower) ||
                       clientNit.includes(searchLower);
            });
        },
        // 2. PAGINACIÓN: Muestra solo los elementos de la página actual
        pagedRows() {
            const start = (this.currentPage - 1) * this.itemsPerPage;
            const end = start + this.itemsPerPage;
            return this.filteredRows.slice(start, end);
        },
        // 3. Totales
        totalPages() {
            return Math.ceil(this.filteredRows.length / this.itemsPerPage);
        },
        // 4. Mostrar solo algunas páginas en el menú
        visiblePages() {
            const range = 2; // Mostrar 2 páginas antes y 2 después de la actual
            let start = Math.max(1, this.currentPage - range);
            let end = Math.min(this.totalPages, this.currentPage + range);

            const pages = [];
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            return pages;
        }
    },
    watch: {
        // Al cambiar el término de búsqueda, volvemos a la página 1
        searchTerm() {
            this.currentPage = 1;
        }
    },
    mounted() {
        this.fetchClientsAndInvoices();
    },
    methods:{
        // === 1. Lógica de Carga (Carga TODOS los datos) ===
        async fetchClientsAndInvoices() {
            this.isLoading = true;
            const token = localStorage.getItem("accessToken");
            const config = {
                headers: { 'Authorization': `Bearer ${token}` }
            };

            // Usamos un array para almacenar todos los clientes, pues se necesitan para el mapeo
            const clientMap = {};

            // 1. Cargar Clientes
            try {
                const clientsResult = await axios.get(this.$store.state.apiURL + 'clients', config);
                this.clients = clientsResult.data;
                this.clientsBulk= this.clients;
                this.clients.forEach(client => {
                    clientMap[client.id] = {
                        client_name: client.name || client.business_name,
                        client_nit: client.nit,
                        client_type: client.type
                    };
                });
            } catch (error) {
                this.handleSessionError(error);
                this.isLoading = false;
                return;
            }

            // 2. Cargar TODAS las Facturas (sin parámetros de paginación)
            axios.get(this.$store.state.apiURL + 'invoice_headers?company='+companyId, config)
                .then((result) => {
                    const invoicesData = result.data; // Asumimos que es el array completo

                    this.invoices = invoicesData.map(invoice => {
                        const clientInfo = clientMap[invoice.client_id] || {};

                        // Conversión de tipos (necesaria)
                        const subtotal = parseFloat(invoice.subtotal);
                        const sub_total_brut = parseFloat(invoice.sub_total_brut);
                        const total_taxes = parseFloat(invoice.total_taxes);
                        const total_discount= parseFloat(invoice.total_discount);
                        const total = parseFloat(invoice.total);

                        return {
                            ...invoice,
                            subtotal,
                            total_taxes,
                            total,
                            sub_total_brut,
                            total_discount,
                            // Campos enriquecidos para la tabla
                            client_name: invoice.client_name || clientInfo.client_name || 'N/A',
                            client_nit: invoice.client_nit || clientInfo.client_nit || 'N/A',
                            client_type: clientInfo.client_type || 'N/A'
                        };
                    });

                    // 🛑 PASO CLAVE: Establecer el total basado en lo que se cargó
                    this.totalRecords = this.invoices.length;

                })
                .catch(error => {
                    this.handleSessionError(error);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        // === 2. Controladores de la Paginación y Redirección ===
        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
            }
        },
        toCreate() {
             this.$router.push('/create-invoice');
        },
        // Manejo de error de sesión (adaptado de tu código)
        handleSessionError(error) {
            const self = this;
            if (error.response && error.response.status === 401) {
                 self.$swal({
                    title: 'Sesión',
                    text: 'El tiempo de sesión a vencido, ingresa nuevamente.',
                    icon:'warning',
                    showCancelButton: false,
                    confirmButtonColor:'#bf4f51',
                    showCloseButton: true,
                    showLoaderOnConfirm: false
                }).then(function(){
                    localStorage.removeItem('user');
                    localStorage.removeItem('accessToken');
                    self.$router.push('/signin');
                });
            } else {
                 console.error("Error al obtener datos:", error);
            }
        },
        // Dentro de methods en InvoiceTablesView.vue

        // En InvoiceTablesView.vue -> methods:

        async descargarTodosLosPDFs() {
            // 1. Filtrar solo las emitidas
            const rowsToProcess = this.filteredRows.filter(inv => inv.state === 'EMITTED');

            if (rowsToProcess.length === 0) {
                return this.$swal('Sin facturas', 'No hay facturas con estado "Emitida" para procesar.', 'info');
            }

            // 2. Solicitar Rango (Del - Al)
            const { value: range } = await this.$swal({
                title: 'Configurar Exportación Masiva',
                html: `
                    <p>Se encontraron ${rowsToProcess.length} facturas emitidas.</p>
                    <div style="display: flex; gap: 10px; justify-content: center; margin-top: 15px;">
                        <div>
                            <label>Desde (Indice):</label>
                            <input id="swal-range-start" type="number" class="swal2-input" value="1" min="1" max="${rowsToProcess.length}">
                        </div>
                        <div>
                            <label>Hasta (Indice):</label>
                            <input id="swal-range-end" type="number" class="swal2-input" value="${rowsToProcess.length}" min="1" max="${rowsToProcess.length}">
                        </div>
                    </div>
                    <small class="text-muted">Útil si la descarga se detuvo por error anteriormente.</small>
                `,
                focusConfirm: false,
                showCancelButton: true,
                confirmButtonText: 'Iniciar Exportación',
                preConfirm: () => {
                    const start = parseInt(document.getElementById('swal-range-start').value);
                    const end = parseInt(document.getElementById('swal-range-end').value);
                    if (start > end || start < 1 || end > rowsToProcess.length) {
                        this.$swal.showValidationMessage(`Rango inválido. Debe estar entre 1 y ${rowsToProcess.length}`);
                    }
                    return { start, end };
                }
            });

            if (!range) return;

            // Ajustar el array según el rango seleccionado (índice humano a índice de array)
            const finalSelection = rowsToProcess.slice(range.start - 1, range.end);
            const totalToProcess = finalSelection.length;

            // 3. Mostrar Barra de Progreso
            this.$swal({
                title: 'Exportando Documentos',
                html: `
                    <div class="progress-wrapper">
                        <div class="progress-info">
                            <span id="progress-text" class="progress-percentage">Iniciando...</span>
                        </div>
                        <div class="progress">
                            <div id="progress-bar" class="progress-bar bg-success" role="progressbar" style="width: 0%"></div>
                        </div>
                    </div>
                    <p id="progress-counter" class="mt-3">0 de ${totalToProcess} procesados</p>
                `,
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    this.$swal.showLoading();
                }
            });

            try {
                let current = 0;
                for (const inv of finalSelection) {
                    current++;

                    // Actualizar UI de la barra de progreso
                    const percent = Math.round((current / totalToProcess) * 100);
                    const progressBar = document.getElementById('progress-bar');
                    const progressText = document.getElementById('progress-text');
                    const progressCounter = document.getElementById('progress-counter');

                    if (progressBar) progressBar.style.width = `${percent}%`;
                    if (progressText) progressText.innerText = `${percent}%`;
                    if (progressCounter) progressCounter.innerText = `${current} de ${totalToProcess} procesados`;

                    // Proceso de generación
                    const invoiceDetails = await this.getInvoiceDetails(inv.id);
                    const clientData = this.clientsBulk.find(c => c.id === inv.client_id) || {};

                    const facturaData = {
                        header: {
                            ...inv,
                            client_name: inv.client_name || clientData.name,
                            client_nit: inv.client_nit || clientData.nit,
                            client_nrc: inv.client_nrc || clientData.nrc || 'N/A',
                            client_address: clientData.fiscal_address || clientData.address || 'Sin dirección',
                            client_phone: clientData.phone || 'N/A',
                            client_email: clientData.email || 'N/A',
                            client_department: clientData.department,
                            client_municipality: clientData.municipality,
                            client_activity_code: clientData.activity_code || '99999',
                            client_activity_desc: clientData.activity_desc || 'Venta al consumidor',
                            client_document_type: clientData.document_type || '36',
                            full_number: inv.full_number,
                            date_issued: inv.date_issued,
                            dte_type: inv.dte_type,
                            subtotal: parseFloat(inv.subtotal),
                            total_taxes: parseFloat(inv.total_taxes),
                            total: parseFloat(inv.total),
                            sub_total_brut: parseFloat(inv.sub_total_brut || inv.subtotal),
                            retention_amount: parseFloat(inv.retention_amount || 0)
                        },
                        lines: invoiceDetails.map(line => ({
                            ...line,
                            quantity: parseFloat(line.quantity),
                            unit_price: parseFloat(line.unit_price),
                            sub_total_brut: parseFloat(line.sub_total_brut),
                            line_total: parseFloat(line.line_total)
                        }))
                    };

                    await useDTEFiscal(facturaData, this.companyConfig, false, true);

                    // Delay para evitar bloqueos del navegador
                    await new Promise(resolve => setTimeout(resolve, 800));
                }

                this.$swal.close();
                this.$swal('¡Completado!', `Se exportaron ${totalToProcess} archivos con éxito.`, 'success');

            } catch (error) {
                console.error("Error en proceso masivo:", error);
                this.$swal('Error en la descarga', 'El proceso se detuvo. Puedes continuar desde el último índice procesado.', 'error');
            }
        },

        /**
         * Trae los items de una factura específica desde el API
         */
        async getInvoiceDetails(invoiceId) {
            const token = localStorage.getItem("accessToken");
            const apiURL = this.$store.state.apiURL;
            try {
                // Asumiendo que tienes un endpoint que devuelva invoice_lines por invoice_id
                const response = await axios.get(`${apiURL}invoice_lines?invoice_id=${invoiceId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                return response.data; // Retorna el array de líneas
            } catch (e) {
                console.error("Error al obtener detalles de factura " + invoiceId, e);
                return [];
            }
        }
    },
};
</script>